import { useEffect, useState } from 'react';

import { Button } from '@/components/PageHeading';
import {
  documentPapers,
  pageRule,
  paperSpec,
  rememberPaper,
  rememberedPaper,
  type PaperSize,
  shopPaperOr,
} from './paper';

/**
 * The paper picker, the `@page` rule, and the sheet the document sits on.
 *
 * <p>
 * <strong>The rule is written into a `&lt;style&gt;` element rather than set through a class,
 * because `@page` cannot be scoped.</strong> It is a document-level at-rule with no selector, so
 * there is no way to have one apply and another not — a component that offers a choice of paper has
 * to emit the chosen rule and only that one. Without it the browser prints whatever the driver last
 * had, which on a thermal roll means an A4 page's worth of blank paper after every receipt.
 * </p>
 *
 * <p>
 * The choice is remembered per device, not per tenant: the paper is a property of the machine in
 * front of this person. A chain whose head office prints A4 and whose branch has a roll beside the
 * till would be wrong under one shared value.
 * </p>
 *
 * <p>
 * <strong>And remembered per document, which is the other half of the same argument.</strong> One
 * machine has two habits: the customer copy goes on A4 or a roll, the tailor's docket on a half
 * sheet. Under a single key, printing one would silently re-pick the other's paper, and a clerk
 * would be re-choosing A4 every morning without ever working out why.
 * </p>
 */
export function PrintFrame({
  children,
  onClose,
  title,
  sizes = documentPapers,
  remember,
  start,
  extra,
}: {
  /** Called with the chosen size, so the document can lay itself out for it. */
  children: (paper: PaperSize) => React.ReactNode;
  onClose: () => void;
  title: string;
  /** What this document offers. Omit for the roll, A5 and A4 (`documentPapers`). */
  sizes?: readonly PaperSize[];
  /** Storage key suffix, so two documents on one machine keep their own habits. */
  remember?: string;
  /**
   * The paper the document itself asks for — a report's, from its layout (`reportPaperFor`). It
   * opens on this every time and the choice is not remembered: the layout decides, not a habit.
   */
  start?: PaperSize;
  /** Rendered beside the paper picker — the docket's language chooser, today. */
  extra?: React.ReactNode;
}) {
  const storageKey = remember ? `attirerent.print.paper.${remember}` : undefined;
  // The shop's starting paper, until this machine has a habit of its own for this document.
  const fallback = shopPaperOr(sizes.includes('A5') ? 'A5' : (sizes[0] ?? 'A5'), sizes);

  const [paper, setPaper] = useState<PaperSize>(() =>
    start && sizes.includes(start)
      ? start
      : storageKey
        ? rememberedPaper(fallback, storageKey, sizes)
        : rememberedPaper(fallback, undefined, sizes),
  );

  useEffect(() => {
    if (!start) {
      rememberPaper(paper, storageKey);
    }
  }, [paper, storageKey, start]);

  const spec = paperSpec(paper);
  const options = sizes.map((size) => paperSpec(size));

  return (
    <>
      {/* Not in style.css: the rule has to change with the choice, and @page takes no selector. */}
      <style>{pageRule(paper)}</style>

      <div className="mb-3 flex flex-wrap items-center gap-2 print:hidden lg:mb-4">
        <Button icon="icon icon-arrow-left" shortcut="back" onClick={onClose}>
          Back
        </Button>

        <div
          role="group"
          aria-label="Paper size"
          className="border-border-color flex overflow-hidden rounded-md border bg-white"
        >
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setPaper(option.id)}
              aria-pressed={option.id === paper}
              title={option.hint}
              className={`cursor-pointer px-3 py-1.5 text-[13px] whitespace-nowrap ${
                option.id === paper
                  ? 'bg-dark font-medium text-white'
                  : 'hover:bg-light text-gray-900'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <span className="text-default text-[12.5px]">{spec.hint}</span>

        {extra}

        {/*
          Alt+P rather than the browser's own Ctrl+P, and the difference matters here: this goes
          through `window.print()` *after* the chosen `@page` rule is in the document, so a receipt
          prints on the roll it was laid out for. Ctrl+P still works and still prints whatever the
          driver last had.
        */}
        <Button
          variant="primary"
          icon="icon icon-printer"
          shortcut="print"
          onClick={() => window.print()}
        >
          Print
        </Button>
      </div>

      <p className="text-default sr-only print:hidden">{title}</p>

      <div className="bg-light border-border-color overflow-x-auto rounded-md border p-3 print:border-0 print:bg-white print:p-0">
        {/* The sheet. Its width is the paper's, so the preview is the print at true size. */}
        <div className="mx-auto" style={{ width: spec.width, maxWidth: '100%' }}>
          {children(paper)}
        </div>
      </div>
    </>
  );
}
